import { Button, Checkbox, Search } from '@carbon/react';
import { Add, TrashCan } from '@carbon/icons-react';
import {
  asProblem,
  crudApi,
  EmptyState,
  FormModal,
  hasPermission,
  ListTable,
  problemMessage,
  RowAction,
  useAdmin,
  type RecordValues,
} from '@pos/admin-ui';
import { fold, useTranslation } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import './pricing.scss';

interface Menu {
  id: string;
  version: number;
  code: string;
  name: string;
}

interface Link {
  id: string;
  version: number;
  menuId: string;
}

/**
 * The menus a price list prices (ADR 0017). Several can be linked at once; a price list linked to no menu prices any
 * menu that has no price list of its own.
 */
export function PriceListMenusTab({ priceList }: { priceList: RecordValues }) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const canEdit = hasPermission(session, 'ho.menu.price_lists.manage');
  const id = String(priceList.id);
  const queryClient = useQueryClient();
  const linksApi = crudApi<Link & { [key: string]: unknown }, never>(hoApi, `/api/v1/menu/price-lists/${id}/menus`);
  const key = ['ho', 'price-list-menus', id];
  const links = useQuery({ queryKey: key, queryFn: () => linksApi.list({ pageSize: 500 }) });
  const menus = useQuery({
    queryKey: ['ho', 'brand-menus', priceList.brandId],
    queryFn: () =>
      crudApi<Menu & { [key: string]: unknown }, never>(hoApi, '/api/v1/menu/menus').list({
        brandId: String(priceList.brandId),
        pageSize: 500,
        sort: 'code',
      }),
  });
  const [adding, setAdding] = useState(false);

  const remove = useMutation({
    mutationFn: (link: Link) => linksApi.remove(link as Link & { [key: string]: unknown }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
  });

  const byId = new Map((menus.data?.items ?? []).map((m) => [m.id, m]));
  const linked = (links.data?.items ?? [])
    .map((l) => ({ link: l, menu: byId.get(l.menuId) }))
    .sort((a, b) => (a.menu?.code ?? '').localeCompare(b.menu?.code ?? ''));

  const error = links.error ?? menus.error ?? remove.error;
  const addButton = canEdit && (
    <Button kind="tertiary" renderIcon={Add} onClick={() => setAdding(true)}>
      {t('priceMenus.add')}
    </Button>
  );
  const rows = linked.map(({ link, menu }) => ({
    id: link.id,
    link,
    code: menu?.code ?? '—',
    name: menu?.name ?? link.menuId,
  }));

  return (
    <div className="pos-price-table">
      <p className="pos-price-table__hint">
        {linked.length > 0 ? t('priceMenus.hintLinked') : t('priceMenus.hintNone')}
      </p>
      <h2 className="pos-price-table__group-title">
        {t('priceMenus.title')} <span className="pos-price-table__count">({linked.length})</span>
      </h2>
      <ListTable
        ariaLabel={t('priceMenus.title')}
        loading={links.isLoading || menus.isLoading}
        error={error ? problemMessage(t, asProblem(error)) : undefined}
        onRetry={() => {
          remove.reset();
          void links.refetch();
          void menus.refetch();
        }}
        columns={[
          { key: 'code', header: t('priceTable.columns.code'), className: 'pos-price-table__code' },
          { key: 'name', header: t('priceTable.columns.name') },
        ]}
        rows={rows}
        toolbarActions={addButton || undefined}
        rowActions={
          canEdit
            ? (row) => (
                <RowAction
                  action={t('priceMenus.unlink')}
                  record={row.name}
                  icon={TrashCan}
                  onClick={() => !remove.isPending && remove.mutate(row.link)}
                />
              )
            : undefined
        }
        empty={{
          heading: t('priceMenus.emptyTitle'),
          description: t('priceMenus.hintNone'),
        }}
      />
      {adding && (
        <AddMenusDialog
          priceListId={id}
          menus={menus.data?.items ?? []}
          linked={new Set(linked.map((l) => l.link.menuId))}
          onDone={() => void queryClient.invalidateQueries({ queryKey: key })}
          onClose={() => setAdding(false)}
        />
      )}
    </div>
  );
}

/** Ticks any number of the brand's menus and links them in one go; those already linked are shown ticked. */
function AddMenusDialog({
  priceListId,
  menus,
  linked,
  onDone,
  onClose,
}: {
  priceListId: string;
  menus: Menu[];
  linked: ReadonlySet<string>;
  onDone(): void;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const shown = menus.filter((m) => !search || fold(`${m.code} ${m.name}`).includes(fold(search)));
  const selectable = shown.filter((m) => !linked.has(m.id));
  const toggle = (menuId: string, on: boolean) =>
    setChosen((current) => {
      const next = new Set(current);
      if (on) next.add(menuId);
      else next.delete(menuId);
      return next;
    });

  const add = useMutation({
    mutationFn: async () => {
      const { data, error } = await hoApi.POST('/api/v1/menu/price-lists/{id}/menus/add', {
        params: { path: { id: priceListId } },
        body: { menuIds: [...chosen] },
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  return (
    <FormModal
      title={t('priceMenus.add')}
      primaryText={t('priceMenus.link', { count: chosen.size })}
      disabled={chosen.size === 0}
      pending={add.isPending}
      error={add.error ? problemMessage(t, asProblem(add.error)) : null}
      onSubmit={() => add.mutate()}
      onClose={onClose}
    >
      <div className="pos-price-picker">
        <Search
          labelText={t('admin.search')}
          placeholder={t('priceMenus.search')}
          closeButtonLabelText={t('carbon.clearSearch')}
          value={search}
          // Enter searches; it must not submit the dialog.
          onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="pos-price-picker__bar">
          <Checkbox
            id="menu-picker-all"
            labelText={t('priceTable.selectShown', { count: selectable.length })}
            checked={selectable.length > 0 && selectable.every((m) => chosen.has(m.id))}
            disabled={selectable.length === 0}
            onChange={(_, { checked }) => selectable.forEach((m) => toggle(m.id, checked))}
          />
          <span className="pos-price-picker__count">{t('priceTable.chosen', { count: chosen.size })}</span>
        </div>
        {shown.length > 0 && (
          <ul className="pos-price-picker__list" aria-label={t('priceMenus.title')}>
            {shown.map((m) => (
              <li key={m.id} className="pos-price-picker__row">
                <Checkbox
                  id={`menu-pick-${m.id}`}
                  labelText={
                    <span className="pos-price-picker__label">
                      <span className="pos-price-picker__code">{m.code}</span>
                      <span>{m.name}</span>
                    </span>
                  }
                  checked={linked.has(m.id) || chosen.has(m.id)}
                  disabled={linked.has(m.id)}
                  onChange={(_, { checked }) => toggle(m.id, checked)}
                />
              </li>
            ))}
          </ul>
        )}
        {shown.length === 0 && (
          <EmptyState headingLevel={3} heading={t('admin.noResultsTitle')} description={t('admin.noResultsHint')} />
        )}
      </div>
    </FormModal>
  );
}
